import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchInvoices,
  createRazorpayOrder,
  verifyRazorpayPayment,
  fetchInvoicePdfUrl,
  fetchPaymentReceiptPdfUrl,
  fetchQuotations,
  cancelInvoiceContactJf,
  acceptQuotation,
  rejectQuotation,
  fetchQuotationHistory,
  fetchQuotationPdfUrl,
} from "../api/api";

const STATUS_LABELS = {
  draft: "Draft",
  proposed: "Due",
  paid: "Paid",
  overdue: "Overdue",
  cancelled: "Cancelled",
};

const QUOTATION_STATUS_LABELS = {
  proposed: "Awaiting your decision",
  accepted: "Accepted",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

function daysRemainingLabel(days) {
  if (days === null || days === undefined) return null;
  if (days < 0) return `Overdue since ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`;
  if (days === 0) return "Due today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load the payment widget. Please try again."));
    document.body.appendChild(script);
  });
}

function Billing() {

  const [activeTab, setActiveTab] = useState("invoices");

  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingId, setPayingId] = useState(null);
  const [payMessage, setPayMessage] = useState("");
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [receiptLoadingId, setReceiptLoadingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const [decidingId, setDecidingId] = useState(null);
  const [messageDrafts, setMessageDrafts] = useState({});
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [historyByQuotationId, setHistoryByQuotationId] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [qtnPdfLoadingId, setQtnPdfLoadingId] = useState(null);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");
  const [invoiceFromDate, setInvoiceFromDate] = useState("");
  const [invoiceToDate, setInvoiceToDate] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [invoiceData, quotationData] = await Promise.all([
        fetchInvoices(),
        fetchQuotations().catch(() => []),
      ]);
      setInvoices(invoiceData);
      setQuotations(quotationData);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (invoice) => {
    setPdfLoadingId(invoice.id);
    setError("");
    try {
      const url = await fetchInvoicePdfUrl(invoice.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleViewReceipt = async (invoice) => {
    setReceiptLoadingId(invoice.id);
    setError("");
    try {
      const url = await fetchPaymentReceiptPdfUrl(invoice.latest_payment_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const handleCancelContactJf = async (invoice) => {
    if (!window.confirm(`Request cancellation of ${invoice.invoice_number}? Jain Farms will contact you about it — this doesn't cancel it automatically.`)) {
      return;
    }
    setCancellingId(invoice.id);
    setError("");
    try {
      await cancelInvoiceContactJf(invoice.id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCancellingId(null);
    }
  };

  const handleQuotationDecision = async (quotation, decision) => {
    const message = messageDrafts[quotation.id] || "";
    if (decision === "reject" && !window.confirm(`Reject ${quotation.quotation_number}?`)) {
      return;
    }
    setDecidingId(quotation.id);
    setError("");
    try {
      if (decision === "accept") {
        await acceptQuotation(quotation.id, message);
      } else {
        await rejectQuotation(quotation.id, message);
      }
      setMessageDrafts((prev) => ({ ...prev, [quotation.id]: "" }));
      setHistoryByQuotationId((prev) => {
        const next = { ...prev };
        delete next[quotation.id];
        return next;
      });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDecidingId(null);
    }
  };

  const handleToggleQuotationHistory = async (quotationId) => {
    if (expandedHistoryId === quotationId) {
      setExpandedHistoryId(null);
      return;
    }
    setExpandedHistoryId(quotationId);
    if (historyByQuotationId[quotationId]) return;
    setHistoryLoadingId(quotationId);
    try {
      const data = await fetchQuotationHistory(quotationId);
      setHistoryByQuotationId((prev) => ({ ...prev, [quotationId]: data }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const handleViewQuotationPdf = async (quotation) => {
    setQtnPdfLoadingId(quotation.id);
    setError("");
    try {
      const url = await fetchQuotationPdfUrl(quotation.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setQtnPdfLoadingId(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const payableInvoices = invoices.filter((inv) => inv.status === "proposed" || inv.status === "overdue");
  const pendingAmount = payableInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const nextDue = payableInvoices
    .slice()
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];

  const pendingQuotationsCount = quotations.filter((q) => q.status === "proposed").length;

  const filteredInvoices = invoices.filter((inv) => {
    if (invoiceStatusFilter && inv.status !== invoiceStatusFilter) return false;
    // Filtered on the invoice's own issue date, not due_date — due_date
    // skews toward "Due" invoices, since a paid/cancelled invoice's due
    // date is almost always in the past, so a recent range would silently
    // hide everything but currently-due ones.
    const issueDate = inv.created_at ? inv.created_at.slice(0, 10) : null;
    if (invoiceFromDate && (!issueDate || issueDate < invoiceFromDate)) return false;
    if (invoiceToDate && (!issueDate || issueDate > invoiceToDate)) return false;
    return true;
  });

  const handlePay = async (invoice) => {
    setPayingId(invoice.id);
    setError("");
    setPayMessage("");
    try {
      const order = await createRazorpayOrder(invoice.id);

      if (!order.razorpay_key_id || order.razorpay_order_id.startsWith("order_stub_")) {
        // Online payments aren't configured yet (no RAZORPAY_KEY_ID/SECRET) —
        // same "stub until configured" pattern as the rest of this app.
        // The backend's verify-payment call trusts the client unconditionally
        // in this mode, so this just records the payment directly.
        await verifyRazorpayPayment(invoice.id, {
          razorpay_payment_id: `stub_${Date.now()}`,
          razorpay_order_id: order.razorpay_order_id,
          razorpay_signature: "stub",
        });
        setPayMessage(`Payment recorded for ${invoice.invoice_number} (test mode — online payments not yet configured).`);
        await load();
        setPayingId(null);
        return;
      }

      await loadRazorpayScript();
      const razorpay = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: Math.round(order.amount * 100),
        currency: order.currency,
        name: "Jain Farms",
        description: invoice.invoice_number,
        order_id: order.razorpay_order_id,
        handler: async (response) => {
          try {
            await verifyRazorpayPayment(invoice.id, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPayMessage(`Payment successful for ${invoice.invoice_number}.`);
            await load();
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setPayingId(null);
          }
        },
        modal: {
          ondismiss: () => setPayingId(null),
        },
      });
      razorpay.open();
    } catch (err) {
      setError(errorMessage(err));
      setPayingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="billing-container">

          <h2 className="billing-title">
            Billing
          </h2>

          {loading && <p>Loading billing details…</p>}
          {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}
          {payMessage && <p style={{ color: "#166534", fontWeight: 600 }}>{payMessage}</p>}

          {!loading && (
            <>

              <div className="referral-tabs">
                <button
                  type="button"
                  className={`referral-tab-btn${activeTab === "invoices" ? " active" : ""}`}
                  onClick={() => setActiveTab("invoices")}
                >
                  Invoices
                </button>
                <button
                  type="button"
                  className={`referral-tab-btn${activeTab === "quotations" ? " active" : ""}`}
                  onClick={() => setActiveTab("quotations")}
                >
                  Estimates{pendingQuotationsCount > 0 ? ` (${pendingQuotationsCount})` : ""}
                </button>
              </div>

              {activeTab === "invoices" && (
                <>

                  {/* Top Cards */}

                  <div className="billing-top">

                    <div className="status-card">
                      <p>Pending Amount</p>
                      <h3 className={pendingAmount > 0 ? "due" : "active"}>
                        {formatCurrency(pendingAmount)}
                      </h3>
                      <span>{payableInvoices.length} invoice(s) due</span>
                    </div>

                    <div className="status-card">
                      <p>Next Due</p>
                      <h3 className={nextDue ? "due" : "active"}>
                        {nextDue ? new Date(nextDue.due_date).toLocaleDateString() : "—"}
                      </h3>
                      <span>{nextDue ? nextDue.invoice_number : "No dues pending"}</span>
                    </div>

                  </div>

                  {/* Invoices */}

                  <div className="history-card">

                    <h3>INVOICES</h3>

                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
                      <select value={invoiceStatusFilter} onChange={(e) => setInvoiceStatusFilter(e.target.value)}>
                        <option value="">All Statuses</option>
                        {Object.entries(STATUS_LABELS).filter(([value]) => value !== "draft").map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 400 }}>
                        From
                        <input type="date" value={invoiceFromDate} onChange={(e) => setInvoiceFromDate(e.target.value)} />
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 400 }}>
                        To
                        <input type="date" value={invoiceToDate} onChange={(e) => setInvoiceToDate(e.target.value)} />
                      </label>
                    </div>

                    {filteredInvoices.length === 0 && <p>No invoices match these filters.</p>}

                    {filteredInvoices.map((inv) => (
                      <div className="payment-row" key={inv.id}>

                        <div>
                          <strong>{inv.invoice_number}</strong>
                          {inv.category_name && (
                            <span className="category-badge" style={{ marginLeft: "8px", fontSize: "12px", padding: "2px 8px", borderRadius: "10px", background: "#eef2ff", color: "#4338ca" }}>
                              {inv.category_name}
                            </span>
                          )}
                          {inv.all_plot_numbers && inv.all_plot_numbers.length > 1 && (
                            <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#666" }}>
                              Plots: {inv.all_plot_numbers.join(", ")}
                            </p>
                          )}
                          <p>
                            {inv.fy_label} • Due {new Date(inv.due_date).toLocaleDateString()}
                            {(inv.status === "proposed" || inv.status === "overdue") && inv.days_remaining !== null && inv.days_remaining !== undefined && (
                              <> • <span style={{ color: inv.days_remaining < 0 ? "#dc2626" : "#555" }}>{daysRemainingLabel(inv.days_remaining)}</span></>
                            )}
                          </p>
                        </div>

                        <div className="payment-right">
                          <strong>{formatCurrency(inv.amount)}</strong>
                          <span className={inv.status === "paid" ? "paid" : "unpaid"}>
                            {STATUS_LABELS[inv.status] || inv.status}
                          </span>

                          {(inv.status === "proposed" || inv.status === "overdue") && (
                            <div className="bill-actions">
                              <button
                                className="pay-btn"
                                onClick={() => handlePay(inv)}
                                disabled={payingId === inv.id}
                              >
                                {payingId === inv.id ? "Processing…" : "Pay Now"}
                              </button>
                            </div>
                          )}

                          {inv.status === "overdue" && (
                            <div className="bill-actions">
                              {inv.cancel_requested_at ? (
                                <span style={{ fontSize: "13px", color: "#666" }}>
                                  Cancellation requested — we'll be in touch
                                </span>
                              ) : (
                                <button
                                  className="reject-btn"
                                  onClick={() => handleCancelContactJf(inv)}
                                  disabled={cancellingId === inv.id}
                                >
                                  {cancellingId === inv.id ? "Requesting…" : "Cancel – Contact JF"}
                                </button>
                              )}
                            </div>
                          )}

                          {inv.pdf_available && (
                            <div className="bill-actions">
                              <button
                                className="reject-btn"
                                onClick={() => handleViewPdf(inv)}
                                disabled={pdfLoadingId === inv.id}
                              >
                                {pdfLoadingId === inv.id ? "Opening…" : "View Invoice PDF"}
                              </button>
                            </div>
                          )}

                          {inv.payment_receipt_available && (
                            <div className="bill-actions">
                              <button
                                className="reject-btn"
                                onClick={() => handleViewReceipt(inv)}
                                disabled={receiptLoadingId === inv.id}
                              >
                                {receiptLoadingId === inv.id ? "Opening…" : "View Payment Receipt"}
                              </button>
                            </div>
                          )}
                        </div>

                      </div>
                    ))}

                  </div>

                </>
              )}

              {activeTab === "quotations" && (
                <div className="history-card">

                  <h3>ESTIMATES</h3>

                  {quotations.length === 0 && <p>No estimates yet.</p>}

                  {quotations.map((q) => (
                    <div key={q.id} style={{ borderBottom: "1px solid #eee", padding: "14px 0" }}>

                      <div className="payment-row" style={{ padding: 0, border: "none" }}>

                        <div>
                          <strong>{q.quotation_number}</strong>
                          {q.category_name && (
                            <span className="category-badge" style={{ marginLeft: "8px", fontSize: "12px", padding: "2px 8px", borderRadius: "10px", background: "#eef2ff", color: "#4338ca" }}>
                              {q.category_name}
                            </span>
                          )}
                          {q.all_plot_numbers && q.all_plot_numbers.length > 1 && (
                            <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#666" }}>
                              Plots: {q.all_plot_numbers.join(", ")}
                            </p>
                          )}
                          <p>
                            {q.subject || "Estimate"}
                            {q.expiry_date ? ` • Valid till ${new Date(q.expiry_date).toLocaleDateString()}` : ""}
                          </p>
                        </div>

                        <div className="payment-right">
                          <strong>{formatCurrency(q.total)}</strong>
                          <span className={q.status === "accepted" ? "paid" : "unpaid"}>
                            {QUOTATION_STATUS_LABELS[q.status] || q.status}
                          </span>
                        </div>

                      </div>

                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                        {q.pdf_available && (
                          <button
                            className="pay-btn"
                            onClick={() => handleViewQuotationPdf(q)}
                            disabled={qtnPdfLoadingId === q.id}
                          >
                            {qtnPdfLoadingId === q.id ? "Opening…" : "View Estimate PDF"}
                          </button>
                        )}
                        <button
                          className="reject-btn"
                          onClick={() => handleToggleQuotationHistory(q.id)}
                        >
                          {expandedHistoryId === q.id ? "Hide History" : "History"}
                        </button>
                      </div>

                      {q.status === "proposed" && (
                        <div style={{ marginTop: "10px" }}>
                          <textarea
                            rows={2}
                            placeholder="Optional message to Jain Farms (e.g. reason for rejecting)"
                            value={messageDrafts[q.id] || ""}
                            onChange={(e) => setMessageDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                            style={{ width: "100%", marginBottom: "8px" }}
                          />
                          <div style={{ display: "flex", gap: "10px" }}>
                            <button
                              className="pay-btn"
                              onClick={() => handleQuotationDecision(q, "accept")}
                              disabled={decidingId === q.id}
                            >
                              {decidingId === q.id ? "Submitting…" : "Accept"}
                            </button>
                            <button
                              className="reject-btn"
                              onClick={() => handleQuotationDecision(q, "reject")}
                              disabled={decidingId === q.id}
                            >
                              {decidingId === q.id ? "Submitting…" : "Reject"}
                            </button>
                          </div>
                        </div>
                      )}

                      {expandedHistoryId === q.id && (
                        <div style={{ marginTop: "10px", background: "#fafafa", borderRadius: "8px", padding: "10px" }}>
                          {historyLoadingId === q.id && <p style={{ margin: 0 }}>Loading history…</p>}
                          {historyLoadingId !== q.id && (historyByQuotationId[q.id] || []).length === 0 && (
                            <p style={{ margin: 0, color: "#888" }}>No status changes recorded yet.</p>
                          )}
                          {historyLoadingId !== q.id && (historyByQuotationId[q.id] || []).map((h) => (
                            <div key={h.id} style={{ padding: "6px 0", borderBottom: "1px solid #eee", fontSize: "13px" }}>
                              <strong>
                                {QUOTATION_STATUS_LABELS[h.from_status] || h.from_status || "—"} → {QUOTATION_STATUS_LABELS[h.to_status] || h.to_status}
                              </strong>
                              {" "}<span style={{ color: "#888" }}>{new Date(h.created_at).toLocaleString()}</span>
                              {h.reason && <div style={{ color: "#444", marginTop: "2px" }}>{h.reason}</div>}
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  ))}

                </div>
              )}

            </>
          )}

        </div>

      </div>
    </>
  );
}

export default Billing;
